/*
 * Copyright (c) Castle Point Learning Systems Corporation, 2013-2023, all rights reserved.
 * NOTICE: Dissemination of the information and/or materials contained in this file is strictly
 * forbidden unless prior written permission is obtained from Castle Point Learning Systems Corporation.
 * See LICENSE.txt (part of this package) for the full notice.
 */

import React, { FC, useCallback, useContext, useEffect } from 'react';
import { AssistElement, EvaluationLine } from '../../types/model';
import { Styled as S } from '../VerticalSolver.styled';
import { Key } from 'ts-key-enum';
import { EvaluationContext } from './context/EvaluationContextProvider';
import { useEvaluationNavigation } from './hooks/useEvaluationNavigation';
import { useEvaluationUpdate } from './hooks/useEvaluationUpdate';
import { decorateEditorPlaceholder, isEmptyTree, isLastLine, lastPart, placeCursor } from './utils/evaluationUtils';
import { useEvaluationLineCreation } from './hooks/useEvaluationLineCreation';
import { useEvaluationLineDeletion } from './hooks/useEvaluationLineDeletion';
import { useEvaluationEditorBlur } from './hooks/useEvaluationEditorBlur';
import { debounce } from '../constants';
import { createEditor, ExtendedNotifyObject, Heuristics, MathNodeName, NotifyObject, NotifyUpdateObject } from 'semantic-math-editor';

interface EvaluationLineEditorProps {
    line: EvaluationLine;
    assist: AssistElement[];
}
export const EvaluationLineEditor: FC<EvaluationLineEditorProps> = ({ line, assist }) => {
    const ref = React.useRef<HTMLDivElement>();

    const { bindEditor, unbindEditor, evaluation } = useContext(EvaluationContext);

    const create = useEvaluationLineCreation(line.id, assist);
    const update = useEvaluationUpdate(line.id);
    const navigation = useEvaluationNavigation(line.id);
    const deleteLine = useEvaluationLineDeletion(line.id);
    const blur = useEvaluationEditorBlur();

    useEffect(() => {
        const editor = createEditor(ref.current, false);
        editor.heuristics = Heuristics.getAll();
        editor.setTree(line.tree);
        bindEditor(line.id, editor);
        update(true);
        placeCursor(editor);
        editor.focus();

        return () => {
            unbindEditor(line.id);
            editor.destroy();
        };
        // editor should be created only once
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const notificationDispatcher = useCallback(
        (updateInfo: ExtendedNotifyObject[]) => {
            updateInfo.forEach((update) => {
                if (update === NotifyObject.blur) {
                    blur(true);
                }
                if (update === NotifyObject.focus) {
                    blur(false);
                }
                if (update === NotifyObject.failedBackspace || update === NotifyObject.failedDelete) {
                    if (isEmptyTree(line.tree)) {
                        deleteLine();
                    }
                }
                if (
                    update === NotifyObject.failedNavigationDown ||
                    update === NotifyObject.failedNavigationUp ||
                    update === NotifyObject.failedNavigationLeft ||
                    update === NotifyObject.failedNavigationRight ||
                    update === NotifyObject.failedNavigationHome ||
                    update === NotifyObject.failedNavigationEnd
                ) {
                    navigation(update);
                }
            });
        },
        [blur, deleteLine, navigation, line.tree]
    );

    const updateDispatcher = useCallback(
        (updateInfo: NotifyUpdateObject[]) => {
            updateInfo.forEach((e) => (e.toString() === NotifyUpdateObject.tree ? update() : e.toString() === NotifyUpdateObject.cursor && navigation()));
        },
        [update, navigation]
    );

    useEffect(() => {
        const editor = line.editor;
        if (!editor) {
            return;
        }
        editor.addOnUpdate(updateDispatcher);
        editor.addOnNotify(notificationDispatcher);
        return () => {
            editor.removeOnUpdate(updateDispatcher);
            editor.removeOnNotify(notificationDispatcher);
        };
    }, [line.editor, updateDispatcher, notificationDispatcher]);

    useEffect(() => {
        if (isEmptyTree(line.tree) && line.editor) {
            decorateEditorPlaceholder(line.editor.element);
        }
    }, [line.tree, line.editor]);

    const handlePressEnter = useCallback(
        debounce((e: React.KeyboardEvent) => {
            if (e.shiftKey || isLastLine(line, evaluation)) {
                if (lastPart(line).name !== MathNodeName.placeholder) {
                    create(e.shiftKey);
                }
            } else {
                navigation(NotifyObject.failedNavigationDown);
            }
        }, 50),
        [line, create, evaluation, navigation]
    );

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            if (e.ctrlKey || e.altKey || e.metaKey) {
                return;
            }
            if (e.key === Key.Enter) {
                handlePressEnter(e);
            }
        },
        [handlePressEnter]
    );

    return (
        <S.Line $shift={line.shift} $visible={line.shift !== undefined}>
            <span ref={ref} tabIndex={0} onKeyDown={handleKeyDown} style={{ outline: 'none' }} />
        </S.Line>
    );
};
